import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import type { ReactNode } from 'react';
import type { ManualReferralProfessionalRole } from '../../types/manual-referral.type';

// Feature 101: inline entry for a professional who is not in the app. Saving
// creates a provisional contact — no account, no email sent.
export type NewProfessionalContactFormState = {
  firstName: string;
  lastName: string;
  email: string;
  role: ManualReferralProfessionalRole | '';
};

export const emptyNewProfessionalContact: NewProfessionalContactFormState = {
  firstName: '',
  lastName: '',
  email: '',
  role: '',
};

type NewProfessionalContactFieldsProps = {
  idPrefix: string;
  value: NewProfessionalContactFormState;
  errors: Partial<Record<keyof NewProfessionalContactFormState, string>>;
  onChange: (value: NewProfessionalContactFormState) => void;
};

export const NewProfessionalContactFields = ({
  idPrefix,
  value,
  errors,
  onChange,
}: NewProfessionalContactFieldsProps) => {
  const updateValue = <K extends keyof NewProfessionalContactFormState>(
    key: K,
    nextValue: NewProfessionalContactFormState[K],
  ) => onChange({ ...value, [key]: nextValue });

  return (
    <div className="space-y-4 rounded-md border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs text-gray-500">
        Not on the platform — saving records them as a provisional contact. No account is created
        and no email is sent.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <FieldError message={errors.firstName}>
          <Label htmlFor={`${idPrefix}-first-name`}>First name</Label>
          <Input
            id={`${idPrefix}-first-name`}
            value={value.firstName}
            onChange={(event) => updateValue('firstName', event.target.value)}
          />
        </FieldError>
        <FieldError message={errors.lastName}>
          <Label htmlFor={`${idPrefix}-last-name`}>Last name</Label>
          <Input
            id={`${idPrefix}-last-name`}
            value={value.lastName}
            onChange={(event) => updateValue('lastName', event.target.value)}
          />
        </FieldError>
      </div>
      <FieldError message={errors.email}>
        <Label htmlFor={`${idPrefix}-email`}>Email</Label>
        <Input
          id={`${idPrefix}-email`}
          type="email"
          value={value.email}
          onChange={(event) => updateValue('email', event.target.value)}
          placeholder="Optional"
        />
      </FieldError>
      <FieldError message={errors.role}>
        <fieldset>
          <legend className="text-sm font-medium leading-none">Role</legend>
          <div className="mt-2 flex gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name={`${idPrefix}-role`}
                checked={value.role === 'FINANCIAL_ADVISOR'}
                onChange={() => updateValue('role', 'FINANCIAL_ADVISOR')}
                onClick={() => updateValue('role', 'FINANCIAL_ADVISOR')}
              />
              Financial Advisor
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name={`${idPrefix}-role`}
                checked={value.role === 'SERVICE_PROVIDER'}
                onChange={() => updateValue('role', 'SERVICE_PROVIDER')}
                onClick={() => updateValue('role', 'SERVICE_PROVIDER')}
              />
              Service Provider
            </label>
          </div>
        </fieldset>
      </FieldError>
    </div>
  );
};

const FieldError = ({
  children,
  message,
}: {
  children: ReactNode;
  message?: string;
}) => (
  <div className="space-y-2">
    {children}
    {message && <p className="text-sm text-red-600">{message}</p>}
  </div>
);
